import React, { useState, useEffect } from 'react';
import { useSettings } from '../contexts/SettingsContext';
import { useAuth } from '../contexts/AuthContext';

const UserSettings = () => {
  const { settings, updateSettings, loading, error } = useSettings();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    webhookUrl: '',
    elevenLabsVoiceId: '',
    sttLang: 'en-US',
    elevenLabsApiKey: ''
  });
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (settings) {
      setFormData({
        webhookUrl: settings.webhookUrl || '',
        elevenLabsVoiceId: settings.elevenLabsVoiceId || '',
        sttLang: settings.sttLang || 'en-US',
        elevenLabsApiKey: '' // Don't pre-fill API key for security
      });
    }
  }, [settings]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    
    try {
      // Build payload - only include API key if user entered something
      const settingsToUpdate = {
        webhookUrl: formData.webhookUrl,
        elevenLabsVoiceId: formData.elevenLabsVoiceId,
        sttLang: formData.sttLang
      };
      
      // Only include API key if it's non-empty
      if (formData.elevenLabsApiKey && formData.elevenLabsApiKey.trim() !== '') {
        settingsToUpdate.elevenLabsApiKey = formData.elevenLabsApiKey.trim();
      }

      // Debug logging: log the payload being sent
      console.log('DEBUG: Sending settings update payload:', {
        ...settingsToUpdate,
        elevenLabsApiKey: settingsToUpdate.elevenLabsApiKey ? '***REDACTED***' : undefined
      });
      
      const result = await updateSettings(settingsToUpdate);
      if (result.success) {
        setMessage('Settings updated successfully!');
        // Clear the API key field after successful save for security
        setFormData(prev => ({ ...prev, elevenLabsApiKey: '' }));
      } else {
        setMessage(result.message || 'Failed to update settings');
      }
    } catch (err) {
      console.error('DEBUG: Settings update error:', err);
      setMessage('Failed to update settings');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="text-gray-400">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h1 className="text-3xl font-bold mb-6">User Settings</h1>

      {error && (
        <div className="bg-red-500 text-white p-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      {message && (
        <div className={`p-3 rounded-lg mb-4 ${
          message.includes('success') 
            ? 'bg-green-500 text-white' 
            : 'bg-yellow-500 text-black'
        }`}>
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-gray-800 p-6 rounded-lg">
          <h2 className="text-xl font-semibold mb-4">Webhook Configuration</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Webhook URL (HTTPS required)
              </label>
              <input
                type="url"
                name="webhookUrl"
                value={formData.webhookUrl}
                onChange={handleChange}
                placeholder="https://your-n8n-webhook-url.com/webhook/..."
                className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400"
                pattern="https://.*"
              />
              <p className="text-sm text-gray-400 mt-1">
                Enter your n8n webhook URL for AI responses
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gray-800 p-6 rounded-lg">
          <h2 className="text-xl font-semibold mb-4">Voice Settings</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                ElevenLabs Voice ID
              </label>
              <input
                type="text"
                name="elevenLabsVoiceId"
                value={formData.elevenLabsVoiceId}
                onChange={handleChange}
                placeholder="Voice ID for text-to-speech (leave blank to keep current)"
                className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Speech-to-Text Language
              </label>
              <select
                name="sttLang"
                value={formData.sttLang}
                onChange={handleChange}
                className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white"
              >
                <option value="hi-IN">Hindi (India)</option>
                <option value="en-US">English (US)</option>
                <option value="en-IN">English (India)</option>
                <option value="en-GB">English (UK)</option>
                <option value="es-ES">Spanish (Spain)</option>
                <option value="fr-FR">French (France)</option>
                <option value="de-DE">German (Germany)</option>
                <option value="it-IT">Italian (Italy)</option>
                <option value="pt-BR">Portuguese (Brazil)</option>
                <option value="ru-RU">Russian (Russia)</option>
                <option value="ja-JP">Japanese (Japan)</option>
                <option value="ko-KR">Korean (Korea)</option>
                <option value="zh-CN">Chinese (Simplified)</option>
                <option value="zh-TW">Chinese (Traditional)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                ElevenLabs API Key
              </label>
                <input
                type="password"
                name="elevenLabsApiKey"
                value={formData.elevenLabsApiKey}
                onChange={handleChange}
                placeholder="Enter new API key (leave blank to keep current key)"
                className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400"
              />
              {settings?.elevenLabsApiKeyMasked && (
                <p className="text-sm text-gray-400 mt-1">
                  Current API Key: {settings.elevenLabsApiKeyMasked}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-4">
          <button
            type="submit"
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Save Settings
          </button>
        </div>
      </form>
    </div>
  );
};

export default UserSettings;
